<script setup lang="ts">
import { Strategy } from '@/types'
import { useUserStore } from '@/stores/UserStore'
import StrategyCard from './StrategyCard.vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Info } from 'lucide-vue-next'

const { t } = useI18n()
const user = useUserStore()

const onSuccess = (strategies: Array<Strategy>) => {
    toast.success(t('strategies.messages.strategyUpdated'))
    user.updateStrategies(strategies)
}

const onError = (message: string) => {
    toast.error(message)
}
</script>

<template>
    <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
        <h1 class="text-3xl font-bold tracking-tight flex items-center gap-2">
            {{ t('strategies.resell.title') }}
            <Tooltip>
                <TooltipTrigger as-child>
                    <Info class="size-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="bottom" class="max-w-sm text-xs leading-relaxed">
                    {{ t('strategies.resell.subTitle') }}
                </TooltipContent>
            </Tooltip>
        </h1>
    </div>

    <Accordion type="multiple" class="mt-6" :default-value="[user.strategies?.[0]?.id.toString() ?? '']">
        <StrategyCard
            v-for="strategy in user.strategies"
            :key="strategy.id"
            :strategy="strategy"
            @success="onSuccess"
            @error="onError"
        />
    </Accordion>

    <Accordion
        v-if="user.profile?.configuration.store_slug === 'twist'"
        type="multiple"
        class="mt-6"
    >
        <AccordionItem value="buybox">
            <AccordionTrigger class="text-base">Stategia per Buybox</AccordionTrigger>
            <AccordionContent>
                <div class="text-muted-foreground">
                    If the seller with the Buybox is not me, then I want the suggested price to be
                    <code class="text-sm font-semibold mx-2 text-foreground">0.1 EUR</code> lower than the Buybox price
                </div>
            </AccordionContent>
        </AccordionItem>
    </Accordion>

    <Accordion
        v-if="user.profile?.configuration.store_slug === 'valyuu'"
        type="multiple"
        class="mt-6"
    >
        <AccordionItem value="fast-movers">
            <AccordionTrigger class="text-base">Fast movers</AccordionTrigger>
            <AccordionContent>
                <div class="text-muted-foreground">
                    When <code class="text-sm font-semibold mx-2 text-foreground">UnitSoldLast30days >= 10</code> and
                    <code class="text-sm font-semibold mx-2 text-foreground">Stock > 1</code> and
                    <code class="text-sm font-semibold mx-2 text-foreground"
                        >DailyConsumptionRateLast30days * stock < 30 days</code
                    >
                    I want the suggested price to be
                    <code class="text-sm font-semibold mx-2 text-foreground"
                        >1 {{ user.extra?.catalog_currency_symbol }} lower than the Buybox price</code
                    >
                    but
                    <code class="text-sm font-semibold mx-2 text-foreground">higher or equal than 35% minimum margin</code>
                </div>
            </AccordionContent>
        </AccordionItem>

        <AccordionItem value="slow-movers">
            <AccordionTrigger class="text-base">Slow movers</AccordionTrigger>
            <AccordionContent>
                <div class="text-muted-foreground">
                    When <code class="text-sm font-semibold mx-2 text-foreground">AveragePurchasePriceAllTime is blank</code> or
                    <code class="text-sm font-semibold mx-2 text-foreground">( UnitSoldLast30days < 1 and Stock > 0 )</code>
                    I want the suggested price to be
                    <code class="text-sm font-semibold mx-2 text-foreground"
                        >1 {{ user.extra?.catalog_currency_symbol }} lower than the Buybox price</code
                    >
                </div>
            </AccordionContent>
        </AccordionItem>

        <AccordionItem value="good-stock">
            <AccordionTrigger class="text-base">Good stock</AccordionTrigger>
            <AccordionContent>
                <div class="text-muted-foreground">
                    When <code class="text-sm font-semibold mx-2 text-foreground">UnitSoldLast30days > 5</code> and
                    <code class="text-sm font-semibold mx-2 text-foreground">Stock > 0</code> and
                    <code class="text-sm font-semibold mx-2 text-foreground"
                        >DailyConsumptionRateLast30days * stock > 30 days</code
                    >
                    I want the suggested price to be
                    <code class="text-sm font-semibold mx-2 text-foreground"
                        >1 {{ user.extra?.catalog_currency_symbol }} lower than the Buybox price</code
                    >
                    but
                    <code class="text-sm font-semibold mx-2 text-foreground">higher or equal than 30% minimum margin</code>
                </div>
            </AccordionContent>
        </AccordionItem>
    </Accordion>
</template>
